
import React from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Edit } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ProgramDetailsSkeleton } from "@/components/ui/program-details-skeleton";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getFormattedDateTime } from "@/utils";

const PerkPointProgramDetails = () => {
  const { id } = useParams<{ id: string }>();

  // Fetch program data from Supabase
  const { data: program, isLoading, error } = useQuery({
    queryKey: ['program', id],
    queryFn: async () => {
      console.log('Fetching program details for id:', id);
      const { data, error } = await supabase
        .from('programs')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) {
        console.error('Error fetching program:', error);
        throw error;
      }
      
      console.log('Fetched program:', data);
      return data;
    },
    enabled: !!id
  });

  if (isLoading) {
    return <ProgramDetailsSkeleton />;
  }

  if (error || !program) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="outline" size="sm" asChild className="bg-white">
            <Link to="/perkpoint/programs">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Program Not Found</h1>
            <p className="text-gray-500">The program you're looking for doesn't exist or has been removed.</p>
          </div>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-center h-32">
              <div className="text-red-600">
                {error ? `Error loading program: ${error.message}` : "Program not found"}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with back button */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" asChild className="bg-white">
            <Link to="/perkpoint/programs">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">{program.name}</h1>
            <p className="text-gray-500">Program details and management</p>
          </div>
        </div>
        <Button className="bg-green-600 hover:bg-green-700" asChild>
          <Link to={`/perkpoint/edit-program/${program.id}`}>
            <Edit className="mr-2 h-4 w-4" />
            Edit Program
          </Link>
        </Button>
      </div>

      {/* Status badge */}
      <div className="flex items-center gap-2">
        <div className={`px-3 py-1 rounded-full text-sm font-medium ${
          program.status === "Active" 
            ? "bg-green-100 text-green-800" 
            : program.status === "Inactive" 
            ? "bg-gray-100 text-gray-800"
            : "bg-yellow-100 text-yellow-800"
        }`}>
          {program.status}
        </div>
        <span className="text-sm text-muted-foreground">
          Created on {getFormattedDateTime(program.created_at)}
        </span>
        <span className="text-sm text-muted-foreground">
          Last updated: {getFormattedDateTime(program.updated_at)}
        </span>
      </div>

      {/* Tabs for different sections */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full md:w-auto md:inline-grid grid-cols-3 md:grid-cols-4 h-auto">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="members">Members</TabsTrigger>
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview" className="mt-6 space-y-6">
          {/* Program details */}
          <Card>
            <CardHeader className="bg-gray-50 border-b">
              <CardTitle>Program Information</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Program Name</h3>
                  <p className="text-base">{program.name}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Program Code</h3>
                  <p className="text-base">{program.code}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Location</h3>
                  <p className="text-base">{program.location || "Not specified"}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">City</h3>
                  <p className="text-base">{program.city || "Not specified"}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Country</h3>
                  <p className="text-base">{program.country || "Not specified"}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Website URL</h3>
                  <p className="text-base">
                    {program.website_url ? (
                      <a href={program.website_url} target="_blank" rel="noopener noreferrer" 
                        className="text-green-600 hover:underline">
                        {program.website_url}
                      </a>
                    ) : (
                      "Not specified"
                    )}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Loyalty information */}
          <Card>
            <CardHeader className="bg-gray-50 border-b">
              <CardTitle>Loyalty Information</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Loyalty Point Name</h3>
                  <p className="text-base">{program.loyalty_point_name || "Points"}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Default Currency</h3>
                  <p className="text-base">{program.default_currency || "USD"}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stats */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Total Enrolled</CardDescription>
                <CardTitle className="text-2xl">{program.enrolled_members || 0} members</CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Completion Rate</CardDescription>
                <CardTitle className="text-2xl">{program.completion_rate || 0}%</CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Active Members</CardDescription>
                <CardTitle className="text-2xl">{Math.round((program.enrolled_members || 0) * 0.7)}</CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Points Issued</CardDescription>
                <CardTitle className="text-2xl">24,500</CardTitle>
              </CardHeader>
            </Card>
          </div>
        </TabsContent>
        
        <TabsContent value="members" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Members</CardTitle>
              <CardDescription>Manage program members and participants</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Members management functionality will be available soon.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="transactions" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Transactions</CardTitle>
              <CardDescription>View and manage program transactions</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Transaction management functionality will be available soon.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="settings" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Settings</CardTitle>
              <CardDescription>Configure program settings and preferences</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Program settings functionality will be available soon.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PerkPointProgramDetails;
